"use client"

import * as React from "react"
import { format } from "date-fns"
import { Calendar as CalendarIcon } from "lucide-react"
import { DateRange } from "react-day-picker"

import { cn } from "../../../utils/helper"
import { Button } from "../Button"
import { Calendar } from "../Calendar"
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "../Popover"
import { DayNotes, notedDayClassNames, notedDayContent, unavailableDays } from "./dayNotes"

interface DateRangePickerProps {
    startDate: Date
    endDate: Date
    setStartDate: (date: Date) => void
    setEndDate: (date: Date) => void
    // Free spaces under each day, and a line above the calendar saying what they're for.
    dayNotes?: DayNotes
    caption?: string
    onMonthChange?: (month: Date) => void
    // The month shown, when the caller keeps it (so free spaces stay in step with it).
    month?: Date
    // Start with nothing chosen, so the first day clicked is the start and the second the end, rather than the
    // clicks adjusting a range the person didn't choose.
    startEmpty?: boolean
}

export function DatePickerWithRange({
  startDate,
    endDate,
    setStartDate,
    setEndDate,
    dayNotes,
    caption,
    onMonthChange,
    month,
    startEmpty = false,
}: DateRangePickerProps) {
  const [date, setDate] = React.useState<DateRange | undefined>(startEmpty ? undefined : {
    from: startDate,
    to: endDate,
  })
  return (
    <div className={cn("grid gap-2")}>
      <Popover>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant={"outline"}
            className={cn(
                "w-[280px] justify-start text-left font-medium text-sm h-12 px-4 border border-gray-300 rounded-md text-gray-700 bg-white hover:bg-gray-100",
                !date && "text-muted-foreground"
              )}
          >
            <CalendarIcon className="mr-2 h-4 w-4" />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, "LLL dd, y")} -{" "}
                  {format(date.to, "LLL dd, y")}
                </>
              ) : (
                format(date.from, "LLL dd, y")
              )
            ) : (
              <span className="text-secondaryText">Select Dates for booking</span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          {caption && <p className="border-b border-gray-200 px-4 py-2 text-xs text-gray-600">{caption}</p>}
          <Calendar
            initialFocus
            mode="range"
            className="bg-gray-50"
            defaultMonth={date?.from}
            onMonthChange={onMonthChange}
            {...(month ? { month } : {})}
            disabled={unavailableDays(dayNotes)}
            {...(dayNotes ? { classNames: notedDayClassNames, components: { DayContent: notedDayContent(dayNotes) } } : {})}
            selected={date}
            onSelect={
              (newDate) => {
                // Clicking the only day of a one-day range again would clear it, leaving the picker empty while the
                // caller keeps its dates; the range stays instead, so both always show the same dates.
                if (!newDate?.from) return;
                setDate(newDate);
                setStartDate(newDate.from);
                setEndDate(newDate.to!);
              }
            }
            numberOfMonths={2}
          />
        </PopoverContent>
      </Popover>
    </div>
  )
}
